import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ApiApprovalRequestStatus,
  getRequestStatusTitle,
} from "@/types/approvals/approval.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiOfferRequest } from "@/types/recruit/offer.types";
import {
  Avatar,
  DropdownMenu,
  DropdownMenuItem,
  Stack,
  Table,
  TableCell,
  Tag,
  TagVariants,
  Text,
  Title,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface OffersTableProps {
  data?: ApiOfferRequest[];
  isLoading: boolean;
  isFetching: boolean;
  onView?: (request: ApiOfferRequest) => void;
  onEdit?: (request: ApiOfferRequest) => void;
  onCancel?: (id: number) => void;
  onDelete?: (id: number) => void;
  showNotFoundError?: boolean;
  notFoundComponent?: ReactNode;
  emptyStateComponent?: ReactNode;
}

export function OffersTable({
  data,
  isLoading,
  isFetching,
  onView,
  onEdit,
  onCancel,
  onDelete,
  showNotFoundError,
  notFoundComponent,
  emptyStateComponent,
}: OffersTableProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();

  const getOfferRequestStatusColor = (status: ApiApprovalRequestStatus) => {
    let textColor = "";
    let tagVariant: TagVariants = "positive";

    switch (status) {
      case ApiApprovalRequestStatus.approved:
        tagVariant = "positive";
        textColor = "text-positive";
        break;
      case ApiApprovalRequestStatus.awaiting:
        tagVariant = "warning";
        textColor = "text-warning";
        break;
      case ApiApprovalRequestStatus.canceled:
        tagVariant = "secondary";
        textColor = "text-secondary";
        break;
      case ApiApprovalRequestStatus.rejected:
        tagVariant = "danger";
        textColor = "text-danger";
        break;
    }

    return { tagVariant, textColor };
  };

  const columns = useMemo<ColumnDef<ApiOfferRequest>[]>(() => {
    return [
      {
        header: t("ats:candidate"),
        cell: (row) => (
          <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
            <Avatar
              url={row.row.original.offer_data.applicant.photo_url}
              size={"md"}
              acronym={`${row.row.original.offer_data.first_name} ${row.row.original.offer_data.last_name}`}
            />
            <Title
              size={"sm"}
              header={`${row.row.original.offer_data.first_name} ${row.row.original.offer_data.last_name}`}
            />
          </Stack>
        ),
        accessorKey: "offer_data.applicant.id",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:manager"),
        cell: (row) => {
          const manager = row.row.original.offer_data.manager;
          if (!manager) return <Text>-</Text>;
          return (
            <EmployeeHoverCard employee={manager} position={getEmployeePositionTitle(manager)}>
              <Text>{renderEmployeeName(manager)}</Text>
            </EmployeeHoverCard>
          );
        },
        accessorKey: "offer_data.manager.id",
        ...ColumnSizes.standard,
        meta: { disableRowClick: true },
      },
      {
        header: t("ats:job_title"),
        cell: (row) => <Text>{row.row.original.offer_data.job_title || "-"}</Text>,
        accessorKey: "offer_data.job_title",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:location"),
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {row.row.original.offer_data.location?.title || "-"}
          </TableCell>
        ),
        accessorKey: "location.title",
        ...ColumnSizes.standard,
      },
      {
        header: t("ats:planned_start"),
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {row.row.original.offer_data.date_start
              ? new Date(row.row.original.offer_data.date_start).toLocaleDateString()
              : "-"}
          </TableCell>
        ),
        accessorKey: "offer_data.date_start",
        ...ColumnSizes.standard,
      },
      {
        header: t("common:status"),
        cell: (row) => {
          const status = row.row.original.approval_request_details?.status;
          if (!status) {
            return (
              <Stack items="start">
                <>-</>
              </Stack>
            );
          }
          const statusColor = getOfferRequestStatusColor(status);
          return (
            <Stack items="start">
              <Tag variant={statusColor.tagVariant}>{getRequestStatusTitle(status)}</Tag>
            </Stack>
          );
        },
        accessorKey: "approval_request_details.status",
        ...ColumnSizes.status,
      },
    ];
  }, [onView, onEdit, onCancel, onDelete, renderEmployeeName, t]);

  const renderRowAction = useCallback(
    (row: Row<ApiOfferRequest>) => {
      const request = row.original;
      const approvalRequestDetails = request.approval_request_details;
      if (!approvalRequestDetails?.id) {
        return null;
      }

      const hasActions =
        (onView && request.acl?.can_view) ||
        (onEdit && request.acl?.can_edit) ||
        (onCancel && request.acl?.can_cancel) ||
        (onDelete && request.acl?.can_delete);

      if (!hasActions) {
        return null;
      }

      return (
        <DropdownMenu>
          {onView && request.acl?.can_view && (
            <DropdownMenuItem title={t("common:view")} onClick={() => onView(request)} />
          )}
          {onEdit && request.acl?.can_edit && (
            <DropdownMenuItem title={t("ats:edit_offer")} onClick={() => onEdit(request)} />
          )}
          {onCancel && request.acl?.can_cancel && (
            <DropdownMenuItem
              title={`${t("common:cancel")}...`}
              onClick={() => onCancel(approvalRequestDetails.id)}
            />
          )}
          {onDelete && request.acl?.can_delete && (
            <DropdownMenuItem
              variant={"menuDanger"}
              title={`${t("common:delete")}...`}
              onClick={() => onDelete(approvalRequestDetails.id)}
            />
          )}
        </DropdownMenu>
      );
    },
    [onView, onEdit, onCancel, onDelete, t]
  );

  return (
    <Table
      renderRowAction={renderRowAction}
      onRowClick={(row) => row.original.acl?.can_view && onView?.(row.original)}
      data={data || []}
      columns={columns}
      showSkeleton={isLoading}
      isFetching={isFetching}
      stickyHeader
      showNotFoundError={showNotFoundError}
      notFoundComponent={notFoundComponent}
      emptyStateComponent={emptyStateComponent}
    />
  );
}
